import React, { useContext, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Col, Row } from "antd";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";

// Contexts
import { OffsetLayoutContext } from "src/contexts/layout.context";

// Helpers
import { calculateLayout } from "src/helpers/ui.helper";

// Hooks
import useReducerWithLogger from "src/hooks/reducer.hook";

// Services
import * as homePageService from "src/services/home-page.service";
import {
    fetchAddresses,
    parseCarSearchFormToUrlParams,
    transformAddressToOption,
    transformCarSearchLocationToOption,
} from "src/services/booking.service";

// Reducers
import {
    reducer,
    initialState,
    ActionEnum,
} from "src/containers/home/home-page.reducer";

// Components
import CarSearchCard, {
    CarSearchForm,
} from "src/components/booking/CarSearchCard";
import SpecialDealsCarousel from "src/components/home/SpecialDealsCarousel";
import PromotionsCarousel from "src/components/promotions/PromotionsCarousel";
import FindCarBanner from "src/components/home/FindCarBanner";
import WhyUs from "src/components/home/WhyUs";
// import TalkAbousUs from "src/components/home/TalkAbousUs";
import RentForCompanyBanner from "src/components/home/RentForCompanyBanner";
// import ServicesCarousel from "src/components/home/ServicesCarousel";
import LoadingScreen from "src/components/shared/loading/LoadingScreen";
import HomeBanner from "src/components/home/HomeBanner";

const HomePage = () => {
    const history = useHistory();
    const intl = useIntl();

    const offsetLayout = useContext(OffsetLayoutContext);
    const [state, dispatch] = useReducerWithLogger(reducer, initialState);

    const {
        loading,
        locations,
        specialDeals,
        promotions,
        // services,
        // talksAboutUsList,
    } = state;
    const layout = calculateLayout(offsetLayout, 24);

    const locationOptions = useMemo(
        () => locations.map(transformCarSearchLocationToOption),
        [locations]
    );

    useEffect(() => {
        const init = async () => {
            const {
                locations,
                specialDeals,
                promotions,
                services,
                talksAboutUsList,
            } = await homePageService.initialPage();

            dispatch({
                type: ActionEnum.INITIAL_PAGE_COMPLETED,
                payload: {
                    locations,
                    specialDeals,
                    promotions,
                    services,
                    talksAboutUsList,
                },
            });
        };
        init();
    }, [dispatch]);

    const handleFetchAddresses = async (
        value?: string
    ): Promise<SelectOption[]> => {
        const { success, payload } = await fetchAddresses({ location: value });

        if (!success) {
            return [];
        }

        return (payload as SearchAddress[]).map(transformAddressToOption);
    };

    const handleSearchSubmit = (values: CarSearchForm) => {
        const urlParams = parseCarSearchFormToUrlParams(values);
        history.push(`/booking?${urlParams}`);
    };

    const handleClickPromotion = (value: Promotion) => {
        history.push(`/promotions/${value.id}`);
    };

    const handleClickSeeAllPromotions = () => {
        history.push("/promotions");
    };

    const handleClickRentForCompany = () => {
        history.push("/long-term");
    };

    // const handleClickService = (value: Service) => {
    //     console.log(value);
    //     // history.push()
    // };

    // const handleClickSeeAllServices = () => {
    //     console.log("ClickSeeAllPromotions");
    //     // history.push()
    // };

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <DivStyled className="home-page">
            <HomeBanner />

            <Row className="rent-section" justify="start">
                <Col {...layout}>
                    <CarSearchCard
                        fetchAddressOptions={handleFetchAddresses}
                        locationOptions={locationOptions}
                        onSubmit={handleSearchSubmit}
                    />
                </Col>
            </Row>

            {specialDeals.length > 0 && (
                <Row className="special-deals-section" justify="start">
                    <Col span={24}>
                        <SpecialDealsCarousel
                            dataSource={specialDeals}
                            locationOptions={locationOptions}
                            fetchAddressOptions={handleFetchAddresses}
                            onClick={handleSearchSubmit}
                        />
                    </Col>
                </Row>
            )}

            {promotions.length > 0 && (
                <Row className="promotions-section" justify="start">
                    <Col span={24}>
                        <PromotionsCarousel
                            title={intl.formatMessage(
                                { id: "promotionsCarousel.title" },
                                {
                                    total:
                                        promotions.length > 20
                                            ? "20+"
                                            : promotions.length,
                                }
                            )}
                            dataSource={promotions}
                            onClick={handleClickPromotion}
                            onClickSeeAll={handleClickSeeAllPromotions}
                        />
                    </Col>
                </Row>
            )}

            <Row className="find-car-section" justify="start">
                <Col span={24}>
                    <FindCarBanner
                        locationOptions={locationOptions}
                        fetchAddressOptions={handleFetchAddresses}
                        onClick={handleSearchSubmit}
                    />
                </Col>
            </Row>

            <div className="gradient-color-section">
                <Row className="why-us-section" justify="start">
                    <Col span={24}>
                        <WhyUs />
                    </Col>
                </Row>
                {/* <Row className="talk-about-us-section" justify="start">
                    <Col span={24}>
                        <TalkAbousUs dataSource={talksAboutUsList} />
                    </Col>
                </Row> */}

                <Row className="rent-for-company-section" justify="start">
                    <Col span={24}>
                        <RentForCompanyBanner
                            onClick={handleClickRentForCompany}
                        />
                    </Col>
                </Row>

                {/* <Row className="services-section" justify="start">
                    <Col span={24}>
                        {services.length && (
                            <ServicesCarousel
                                dataSource={services}
                                onClick={handleClickService}
                                onClickSeeAll={handleClickSeeAllServices}
                            />
                        )}
                    </Col>
                </Row> */}
            </div>
        </DivStyled>
    );
};

const DivStyled = styled.div`
    &.home-page {
        min-height: calc(100vh - 42px);
        margin-top: -250px;
        padding-top: 124px;
        overflow-x: hidden;

        @media screen and (max-width: 991px) {
            background: #011a40;
            margin-top: -180px;
        }

        .rent-section {
            margin-top: -130px;

            @media screen and (max-width: 991px) {
                margin-top: -20px;
            }

            @media screen and (max-width: 767px) {
                margin-top: 0;
            }
        }

        .special-deals-section {
            margin-top: 120px;

            @media screen and (max-width: 767px) {
                margin-top: 60px;
            }
        }

        .promotions-section {
            margin-top: 110px;

            @media screen and (max-width: 767px) {
                margin-top: 60px;
            }
        }

        .find-car-section {
            margin-top: 173px;

            @media screen and (max-width: 767px) {
                margin-top: 20px;
            }
        }

        .gradient-color-section {
            background: linear-gradient(0deg, #011a40 0%, #3c557a 100%);

            @media screen and (max-width: 767px) {
                background: #011a40;
            }

            .why-us-section {
                padding-top: 141px;

                @media screen and (max-width: 767px) {
                    padding-top: 60px;
                }
            }

            .talk-about-us-section {
                padding-top: 116px;

                @media screen and (max-width: 767px) {
                    padding-top: 60px;
                }
            }

            .rent-for-company-section {
                margin-top: 120px;

                @media screen and (max-width: 767px) {
                    margin-top: 60px;
                }
            }

            .services-section {
                margin-top: 127px;
                padding-bottom: 191px;

                @media screen and (max-width: 767px) {
                    margin-top: 60px;
                    padding-bottom: 60px;
                }
            }
        }
    }
`;

export default HomePage;
